import { useContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import { HookFormContext } from 'components/cef_form/hook-form/providers/HookFormProvider';
import Button from 'components/ui-components/Button';
import { ClassicTick } from 'assets/icons';

const ProgressBar = ({
  stepComponents,
  currentStep,
  setCurrentStep,
  navigate,
  reachedStep,
  validate,
}) => {
  const {
    trigger,
    watch,
    errors,
    watchedFields: {
      companyType,
      businessType,
      companyNumber,
      companyNumberLookupSuccess,
      companyName,
      addressOne,
      addressTwo,
      billingAddressMatchesRegistered,
      ownProperty,
      propertyDuration,
      customer: { title, forename, surname, email, telephone },
      dateOfBirth,
      accountContactName,
      accountContactEmail,
      accountContactPhone,
      purchasingContactName,
      purchasingContactEmail,
      purchasingContactPhone,
    },
  } = useContext(HookFormContext);

  const isContactComplete = (contactName, contactEmail, contactPhone) =>
    contactName ? !!contactName && !!contactEmail && !!contactPhone : true;

  const stepData = {
    1: [
      businessType,
      companyNumber || companyName,
      companyNumber ? companyNumberLookupSuccess || companyName : true,
    ],
    2: [
      addressOne.address_line_1,
      addressOne.postcode,
      companyType === 1 ? (ownProperty, propertyDuration) : true,
      !billingAddressMatchesRegistered
        ? (addressTwo.address_line_1, addressTwo.postcode)
        : true,
    ],
    3: [
      title,
      forename,
      surname,
      email,
      telephone,
      companyType === 1 ? dateOfBirth : true,
      /**
       * TODO: move additional / purchasing contact clicked state in ContactOptions
       * to formConfig and watch that instead of name.
       */
      isContactComplete(
        accountContactName,
        accountContactEmail,
        accountContactPhone,
      ),
      isContactComplete(
        purchasingContactName,
        purchasingContactEmail,
        purchasingContactPhone,
      ),
    ],
  };

  const stepTitles = ['Company', 'Addresses', 'Contact Details', 'Summary'];

  /**
   * A check to see if all previous steps data contain valid values up a certain index
   * @param {number} maxIndex - The maximum index of steps to validate
   * @returns {boolean} - returns true if all previous steps data are valid
   */
  const previousStepDataIsValid = maxIndex => {
    for (let i = 1; i <= maxIndex; i++) {
      if (!stepData[i]?.every(field => field)) {
        return false; // one of the steps contains invalid data
      }
    }
    return true;
  };

  const handleSelectedStep = async selectedStep => {
    // if all previous steps are valid - then we don't need to run any validation on this step
    // just allow the user to go back and edit
    if (previousStepDataIsValid(selectedStep - 1)) {
      setCurrentStep(selectedStep);
      navigate(selectedStep === 5 ? 'summary' : `step${selectedStep}`);
      return;
    }

    if (validate) {
      // call postcode selector validation function
      validate();
      // if no errors, navigate user to selected step
      if (Object.keys(errors).length === 0) {
        setCurrentStep(selectedStep);
        navigate(selectedStep === 5 ? 'summary' : `step${selectedStep}`);
      }
    } else {
      // grab values from form data
      const formData = watch();

      // trigger returns true/false
      // set current step to selected step if res
      const res = await trigger(formData[currentStep + 1]);
      if (res) {
        setCurrentStep(selectedStep);
        navigate(selectedStep === 5 ? 'summary' : `step${selectedStep}`);
      }
    }
  };

  return (
    <div className="register-progress fl ai-c">
      {stepComponents.slice(1).map((step, index) => {
        const stepIndex = index + 1;

        const completeStep =
          stepData[stepIndex]?.every(field => field) && reachedStep > stepIndex;

        return (
          <Fragment key={step.name}>
            <div
              className={`register-progress__button-cont fl fl-col ai-c ${
                stepIndex === currentStep || completeStep ? 'active' : ''
              }`}>
              <p className="register-progress__title">{stepTitles[index]}</p>

              <div
                className="register-progress__step fl jc-c ai-c"
                key={step.name}>
                {completeStep ? (
                  <ClassicTick fillColour="#fff" width={20} height={15} />
                ) : (
                  stepIndex
                )}
              </div>

              <Button
                className={`register-progress__button ${
                  completeStep ? 'edit' : ''
                }`}
                type="button"
                handleClick={() => handleSelectedStep(stepIndex + 1)}
                label={`edit step ${stepTitles[index]}`}>
                Edit
              </Button>
            </div>

            {stepIndex < 4 && (
              <div
                className={`register-progress__connection ${
                  completeStep ? 'active' : ''
                }`}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

ProgressBar.propTypes = {
  stepComponents: PropTypes.array.isRequired,
  currentStep: PropTypes.number.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  reachedStep: PropTypes.number.isRequired,
};

export default ProgressBar;
